import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { formatDateRu } from '../utils/formatUtils';
import PasswordModal from './PasswordModal';
import { 
  Shield,
  Plus,
  Trash2,
  Globe,
  Lock,
  AlertCircle,
  CheckCircle,
  X,
  Key,
  Copy,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccessPage = () => {
  const [ipList, setIpList] = useState([]);
  const { tt } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingIpData, setPendingIpData] = useState(null);
  const [newIp, setNewIp] = useState({
    ip_address: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ipToDelete, setIpToDelete] = useState(null);
  const [emergencyKey, setEmergencyKey] = useState('');
  const [showEmergencyKey, setShowEmergencyKey] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [showEmergencyPasswordModal, setShowEmergencyPasswordModal] = useState(false);

  useEffect(() => {
    fetchIpList();
  }, []);

  const fetchIpList = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ip-whitelist`);
      setIpList(response.data);
    } catch (error) {
      console.error('Error fetching IP list:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmergencyKey = async () => {
    try {
      const response = await axios.get(`${API}/ip-whitelist/emergency-key`);
      setEmergencyKey(response.data.emergency_key);
      setShowEmergencyKey(true);
    } catch (error) {
      console.error('Error fetching emergency key:', error);
    }
  };

  const regenerateKey = async () => {
    try {
      const response = await axios.post(`${API}/ip-whitelist/regenerate-emergency-key`);
      setEmergencyKey(response.data.emergency_key);
      setKeyCopied(false);
    } catch (error) {
      console.error('Error regenerating key:', error);
    }
  };

  const copyEmergencyLink = () => {
    const link = `${window.location.origin}/api/emergency-access?key=${emergencyKey}`;
    navigator.clipboard.writeText(link);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 3000);
  };

  const handleAddIp = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validate IP address format
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipRegex.test(newIp.ip_address)) {
      setErrors({ ip_address: tt('Неверный формат IP адреса') });
      return;
    }

    // Store data and show password modal
    setPendingIpData(newIp);
    setShowAddModal(false);
    setShowPasswordModal(true);
  };

  const confirmAddIp = async () => {
    if (!pendingIpData) return;

    try {
      await axios.post(`${API}/ip-whitelist`, pendingIpData);
      setPendingIpData(null);
      setNewIp({ ip_address: '', description: '' });
      fetchIpList();
    } catch (error) {
      setErrors({ 
        general: error.response?.data?.detail || tt('Ошибка при добавлении IP адреса') 
      });
    }
  };

  const handleDeleteIp = async (ipId) => {
    setIpToDelete(ipId);
    setShowDeleteModal(true);
  };

  const confirmDeleteIp = async () => {
    if (!ipToDelete) return;

    try {
      await axios.delete(`${API}/ip-whitelist/${ipToDelete}`);
      setShowDeleteModal(false);
      setIpToDelete(null);
      fetchIpList();
    } catch (error) {
      console.error('Error deleting IP:', error);
      setShowDeleteModal(false);
      setIpToDelete(null);
    }
  };

  const formatDate = (dateString) => {
    return formatDateRu(dateString);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{tt('Управление доступом')}</h1>
          <p className="page-subtitle">
            {tt('Настройка IP адресов для доступа к системе. Если не добавлено ни одного IP - доступ разрешен с любого адреса.')}
          </p>
        </div>
      </div>

      {/* Current Status */}
      <div className="card p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {ipList.length > 0 ? (
              <>
                <div className="p-3 bg-red-100 rounded-full mr-4">
                  <Lock className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{tt('Доступ ограничен')}</h3>
                  <p className="text-gray-600">
                    {tt('Разрешен доступ только с')} {ipList.length} IP {ipList.length === 1 ? tt('адреса') : tt('адресов')}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="p-3 bg-yellow-100 rounded-full mr-4">
                  <Globe className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{tt('Доступ открыт')}</h3>
                  <p className="text-gray-600">
                    {tt('Доступ к системе разрешен с любого IP адреса')}
                  </p>
                </div>
              </>
            )}
          </div>
          
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary"
            data-testid="add-ip-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            {tt('Добавить IP')}
          </button>
        </div>
      </div>

      {/* Emergency Access */}
      {ipList.length > 0 && (
        <div className="card p-6 mb-6" data-testid="emergency-access-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <div className="p-2 bg-amber-100 rounded-full mr-3">
                <Key className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">{tt('Экстренный доступ')}</h3>
                <p className="text-gray-500 text-sm">{tt('Ссылка для добавления нового IP без входа в CRM')}</p>
              </div>
            </div>
            {!showEmergencyKey ? (
              <button onClick={() => setShowEmergencyPasswordModal(true)} className="btn btn-secondary text-sm" data-testid="show-emergency-key-btn">
                <Key className="w-4 h-4 mr-1" /> {tt('Показать ключ')}
              </button>
            ) : (
              <button onClick={regenerateKey} className="btn btn-secondary text-sm" data-testid="regenerate-key-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> {tt('Новый ключ')}
              </button>
            )}
          </div>
          {showEmergencyKey && emergencyKey && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-2">{tt('Откройте эту ссылку с нового устройства — IP автоматически добавится в белый список:')}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white border border-gray-200 rounded px-3 py-2 text-sm font-mono text-gray-800 break-all">
                  {window.location.origin}/api/emergency-access?key={emergencyKey}
                </code>
                <button onClick={copyEmergencyLink} className="btn btn-primary text-sm whitespace-nowrap" data-testid="copy-emergency-link">
                  {keyCopied ? <><CheckCircle className="w-4 h-4 mr-1" /> {tt('Скопировано')}</> : <><Copy className="w-4 h-4 mr-1" /> {tt('Копировать')}</>}
                </button>
              </div>
              <p className="text-xs text-red-500 mt-2">{tt('Не передавайте ссылку третьим лицам. При утечке — нажмите "Новый ключ".')}</p>
            </div>
          )}
        </div>
      )}

      {/* IP List */}
      {ipList.length > 0 && (
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              {tt('Белый список IP адресов')}
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {tt('IP адрес')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {tt('Описание')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {tt('Добавлен')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {tt('Действия')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ipList.map((ip) => (
                  <tr key={ip.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Shield className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-sm font-mono text-gray-900">{ip.ip_address}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {ip.description || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(ip.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleDeleteIp(ip.id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title={tt('Удалить IP')}
                        data-testid={`delete-ip-${ip.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add IP Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{tt('Добавить IP адрес')}</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewIp({ ip_address: '', description: '' });
                  setErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                {errors.general}
              </div>
            )}
            
            <form onSubmit={handleAddIp}>
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">{tt('IP адрес')} *</label>
                  <input
                    type="text"
                    value={newIp.ip_address}
                    onChange={(e) => setNewIp({...newIp, ip_address: e.target.value})}
                    className="form-input"
                    placeholder="192.168.1.100"
                    required
                    data-testid="ip-address-input"
                  />
                  {errors.ip_address && (
                    <p className="text-red-600 text-sm mt-1">{errors.ip_address}</p>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">{tt('Описание')}</label>
                  <input
                    type="text"
                    value={newIp.description}
                    onChange={(e) => setNewIp({...newIp, description: e.target.value})}
                    className="form-input"
                    placeholder={tt('Офис, дом, и т.д.')}
                    data-testid="ip-description-input"
                  />
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  data-testid="submit-add-ip"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  {tt('Добавить')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewIp({ ip_address: '', description: '' });
                    setErrors({});
                  }}
                  className="btn btn-secondary"
                >
                  {tt('Отмена')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Modal */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPendingIpData(null);
        }}
        onConfirm={confirmAddIp}
        title={tt('Защищенное действие: Добавление IP')}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{tt('Подтверждение удаления')}</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setIpToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                <p className="text-gray-900">
                  {tt('Вы уверены, что хотите удалить этот IP адрес из белого списка?')}
                </p>
              </div>
              <p className="text-gray-600 text-sm">
                {tt('Это действие нельзя отменить. Доступ с этого IP адреса будет заблокирован.')}
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={confirmDeleteIp}
                className="btn bg-red-600 hover:bg-red-700 text-white flex-1"
                data-testid="confirm-delete-ip"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {tt('Удалить')}
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setIpToDelete(null);
                }}
                className="btn btn-secondary"
              >
                {tt('Отмена')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Emergency Access Password Modal */}
      <PasswordModal
        isOpen={showEmergencyPasswordModal}
        onClose={() => setShowEmergencyPasswordModal(false)}
        onConfirm={async () => {
          await fetchEmergencyKey();
          setShowEmergencyPasswordModal(false);
        }}
        title={tt('Экстренный доступ')}
      />
    </div>
  );
};

export default AccessPage;